import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  CreateUserPayload,
  UpdateUserPayload,
  UpdateStoreAccessPayload,
  ResetPasswordPayload,
  ChangeOwnPasswordPayload,
  SetOwnPinPayload,
  UserPage,
  UserPageQuery,
} from "./types";

export function listUserPage(
  query: UserPageQuery,
  signal?: AbortSignal,
): Promise<UserPage> {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
  });
  const search = query.search.trim();
  const role = query.role.trim();
  if (search) params.set("search", search);
  if (role) params.set("role", role);
  if (query.active !== null) params.set("active", String(query.active));

  return apiFetch(`${ENDPOINTS.users.list}?${params}`, { signal });
}

export async function listUsers(signal?: AbortSignal): Promise<UserPage> {
  const limit = 100;
  const users = new Map<string, UserPage["items"][number]>();
  let offset = 0;
  let expectedTotal: number | null = null;

  while (expectedTotal === null || users.size < expectedTotal) {
    const page = await listUserPage(
      { limit, offset, search: "", role: "", active: null },
      signal,
    );
    if (expectedTotal === null) {
      expectedTotal = Number.isFinite(page.total)
        ? Math.max(0, Math.floor(page.total))
        : page.items.length;
    }

    const sizeBeforePage = users.size;
    for (const user of page.items) users.set(user.id, user);
    if (
      page.items.length === 0 ||
      page.items.length < limit ||
      users.size === sizeBeforePage
    ) {
      break;
    }
    offset += limit;
  }

  const items = [...users.values()];
  return { items, total: items.length, limit, offset: 0 };
}

export function createUser(payload: CreateUserPayload): Promise<void> {
  return apiFetch(ENDPOINTS.users.list, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateUser(
  id: string,
  payload: UpdateUserPayload,
): Promise<void> {
  return apiFetch(ENDPOINTS.users.detail(id), {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function updateUserStoreAccess(
  id: string,
  payload: UpdateStoreAccessPayload,
): Promise<void> {
  return apiFetch(ENDPOINTS.users.storeAccess(id), {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deactivateUser(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.users.deactivate(id), { method: "POST" });
}

export function reactivateUser(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.users.reactivate(id), { method: "POST" });
}

export function deleteUser(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.users.detail(id), { method: "DELETE" });
}

export function resetUserPassword(
  id: string,
  payload: ResetPasswordPayload,
): Promise<void> {
  return apiFetch(ENDPOINTS.users.resetPassword(id), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function changeOwnPassword(
  payload: ChangeOwnPasswordPayload,
): Promise<void> {
  return apiFetch(ENDPOINTS.me.password, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function setOwnPin(payload: SetOwnPinPayload): Promise<void> {
  return apiFetch(ENDPOINTS.me.pin, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
